<template>
  <div class="rfqf01-page">
    <StatusBar
      v-bind="status"
      :alarmModel="alarmModel"
      :startLoading="startLoading"
      :endLoading="endLoading"
      @setState="setStateHandle"
      @setAlarmMode="setAlarmModeHandle"
    />

    <div class="chart-area">
      <v-chart class="chart" :options="doseValOp" :autoresize="true" />
      <v-chart class="chart" :options="puValOp" :autoresize="true" />
      <v-chart class="chart" :options="uValOp" :autoresize="true" />
    </div>
  </div>
</template>

<script>
import singleDeviceMixin from '@/mixins/singleDeviceMixin';
import StatusBar from './components/status-bar.vue';
import { formatTimestamp } from '@/utils/utils';

import { lineAreaStyle } from '@/echartOption/lineAreaStyle';
import { graColor } from '@/echartOption/common';
import { setDeviceModCtrlServer, getDevicesParamServer, updateDevicesParams } from '@/api/devices';

import { awaitTo } from '@/utils/utils';
import { isEmpty } from '@/utils/is';

export default {
  name: 'RFQF01',
  mixins: [singleDeviceMixin],
  sockets: {
    '/socket/detail'(res) {
      this.update(res);
    }
  },
  data() {
    return {
      endLoading: false,
      startLoading: false,
      alarmModel: false,
      maxLen: 30,
      status: {
        alarmStatus: 0,
        runState: 9
      },
      dose: [], // β
      pu: [], // 钚浓度
      u: [], // 铀浓度
      doseValOp: lineAreaStyle({
        color: ['#833BBB', '#3368F2'],
        legend: {
          top: 'auto',
          right: '5%',
          icon: 'circle',
          orient: 'vertical',
          textStyle: {
            color: '#fff'
          }
        },
        title: { text: 'β值(Bq/m³)', left: 'center' },
        yAxis: { type: 'value' },
        xAxis: {
          type: 'category',
          data: []
        },
        series: [
          {
            name: 'β值(Bq/m³)',
            type: 'line',
            symbol: 'emptyCircle',
            areaStyle: { color: graColor('rgb(3, 153, 224)', 'rgba(3, 153, 224, 0.1)') },
            smooth: true,
            data: []
          }
        ]
      }),
      puValOp: lineAreaStyle({
        color: ['#833BBB', '#3368F2'],
        legend: {
          top: 'auto',
          right: '5%',
          icon: 'circle',
          orient: 'vertical',
          textStyle: {
            color: '#fff'
          }
        },
        title: { text: '钚浓度(Bq/m³)', left: 'center' },
        yAxis: { type: 'value' },
        xAxis: {
          type: 'category',
          data: []
        },
        series: [
          {
            name: '钚浓度(Bq/m³)',
            type: 'line',
            symbol: 'emptyCircle',
            areaStyle: { color: graColor('rgb(3, 153, 224)', 'rgba(3, 153, 224, 0.1)') },
            smooth: true,
            data: []
          }
        ]
      }),
      uValOp: lineAreaStyle({
        color: ['#833BBB', '#3368F2'],
        legend: {
          top: 'auto',
          right: '5%',
          icon: 'circle',
          orient: 'vertical',
          textStyle: {
            color: '#fff'
          }
        },
        title: { text: '铀浓度(Bq/m³)', left: 'center' },
        yAxis: { type: 'value' },
        xAxis: {
          type: 'category',
          data: []
        },
        series: [
          {
            name: '铀浓度(Bq/m³)',
            type: 'line',
            symbol: 'emptyCircle',
            areaStyle: { color: graColor('rgb(3, 153, 224)', 'rgba(3, 153, 224, 0.1)') },
            smooth: true,
            data: []
          }
        ]
      })
    };
  },
  filters: {
    changeStatus(val) {
      if (!val) return '-';
      let statusDict = {
        6: '测量中',
        9: '空闲',
        29: '纸少于10%',
        30: '缺纸'
      };
      return statusDict[val];
    }
  },
  mounted() {
    this.loadDevicesParams();
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    async loadDevicesParams() {
      const [err, res] = await awaitTo(getDevicesParamServer(this.sn, '1'));

      if (err) {
        return console.error('加载设备参数失败', err);
      }

      if (res.code == 200 && res.data) {
        if (isEmpty(res.data)) return;

        this.resData = res.data;

        const { high, low } = res.data.paramItems[res.data.paramItems.length - 1].value;
        this.item = res.data.paramItems[res.data.paramItems.length - 1];
        if (high == '0' && low == '0') {
          this.alarmModel = false;
          this.status.alarmStatus = 0;
        } else {
          this.alarmModel = true;
          this.status.alarmStatus = 2;
        }
      }
    },
    update(res) {
      //   console.log('res: ', res);
      const { rfqf01Dto } = res.mods.nuclearMod;

      this.status = {
        alarmStatus: res.mods.nuclearMod.state,
        runState: Number(rfqf01Dto.deviceState)
      };

      this.doseValOp.xAxis.data.push(formatTimestamp(res.beginTime));
      this.doseValOp.series[0].data.push(rfqf01Dto.beta);

      this.puValOp.xAxis.data.push(formatTimestamp(res.beginTime));
      this.puValOp.series[0].data.push(rfqf01Dto.pu);

      this.uValOp.xAxis.data.push(formatTimestamp(res.beginTime));
      this.uValOp.series[0].data.push(rfqf01Dto.u);

      if (this.doseValOp.xAxis.data.length > this.maxLen) {
        this.doseValOp.xAxis.data.shift();
        this.doseValOp.series[0].data.shift();

        this.puValOp.xAxis.data.shift();
        this.puValOp.series[0].data.shift();

        this.uValOp.xAxis.data.shift();
        this.uValOp.series[0].data.shift();
      }
    },
    async setStateHandle(state) {
      let obj = {
        deviceType: this.deviceType,
        sn: this.sn,
        currState: state
      };

      const [err, res] = await awaitTo(setDeviceModCtrlServer(obj));

      if (err) {
        return console.error('RFQF01设置失败', err);
      }

      if (res.code == 200 && res.data) {
        this.$message.success('设置成功,请稍后');
        if (state == 1) {
          this.startLoading = true;
          this.startTimer = setTimeout(() => {
            this.startLoading = false;
          }, 1000 * 50);
        } else {
          this.endLoading = true;
          this.endTimer = setTimeout(() => {
            this.endLoading = false;
          }, 1000 * 7);
        }
      }
    },
    async setAlarmModeHandle(val) {
      this.resData.paramItems = [];
      if (val) {
        this.item.value.high = '1';
        this.item.value.low = '1';
      } else {
        this.item.value.high = '0';
        this.item.value.low = '0';
      }

      this.resData.paramItems.push(this.item);

      const [err, res] = await awaitTo(updateDevicesParams(this.resData));
      if (err) {
        return console.error('设置报警模式失败', err);
      }

      if (res.code == 200) {
        // this.$message.success('报警模式设置成功');
        this.loadDevicesParams();
      }
    },
    clearTimer() {
      if (this.startTimer) {
        clearTimeout(this.startTimer);
      }

      if (this.endTimer) {
        clearTimeout(this.endTimer);
      }
    }
  },
  components: {
    StatusBar
  }
};
</script>

<style lang="scss">
.rfqf01-page {
  padding: 10px;
  height: 100%;
  box-sizing: border-box;

  span {
    @include state-textstyle;
  }

  .rfqf01-common-border2 {
    padding: 10px 0;
    box-sizing: border-box;
    border: 1px solid rgb(96, 118, 173);
  }

  .common-border {
    height: 100%;
    padding: 2px;
    background-image: url('~@/assets/image/lt.png'), url('~@/assets/image/rt.png'),
      url('~@/assets/image/lb.png'), url('~@/assets/image/rb.png'),
      url('~@/assets/image/linelight.png');
    background-repeat: no-repeat, no-repeat, no-repeat, no-repeat, no-repeat;
    background-position: top left, top right, bottom left, bottom right, center -5px;

    .comp3-content {
      height: 100%;
      box-sizing: border-box;
    }
  }

  .chart-area {
    display: flex;
    align-items: center;
    justify-content: space-around;
    height: calc(100% - 100px);
    .chart {
      border: 1px solid rgb(96, 118, 173) !important;
      height: 90% !important;
      width: 30% !important;
    }
  }
}
</style>
